/**
 * CSV Import Modal Component
 *
 * Multi-step modal for importing events from CSV:
 * 1. Upload - Drop zone and download template
 * 2. Preview - Review and validate data
 * 3. Results - Success/failure summary
 */

import { useState, useCallback } from 'react';
import { CSVDropZone } from './CSVDropZone';
import { CSVPreviewTable } from './CSVPreviewTable';
import { parseCSV, prepareForAPI, downloadTemplate, ParseResult } from './csvParser';
import { RowValidationResult } from '../../../utils/csvValidation';
import { bulkCreateEvents, Event } from '../../../api/events';
import { getErrorMessage } from '../../../api/client';

type Step = 'upload' | 'preview' | 'results';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResult {
  success: boolean;
  created: number;
  events: Event[];
  error?: string;
}

export function CSVImportModal({ isOpen, onClose, onSuccess }: CSVImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [validationResults, setValidationResults] = useState<RowValidationResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setParseResult(null);
    setValidationResults([]);
    setIsLoading(false);
    setImportResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFileSelect = useCallback(async (file: File) => {
    setIsLoading(true);
    const result = await parseCSV(file);
    setParseResult(result);
    setValidationResults(result.validationResults);
    setIsLoading(false);

    if (result.success && result.validationResults.length > 0) {
      setStep('preview');
    }
  }, []);

  const handleRemoveRow = useCallback((rowIndex: number) => {
    setValidationResults((prev) => prev.filter((r) => r.rowIndex !== rowIndex));
  }, []);

  const handleImport = useCallback(async () => {
    const validRows = validationResults.filter((r) => r.isValid);
    if (validRows.length === 0) return;

    setIsLoading(true);
    try {
      const events = prepareForAPI(validationResults);
      const response = await bulkCreateEvents(events);
      setImportResult({
        success: true,
        created: response.created,
        events: response.events,
      });
      setStep('results');
      onSuccess();
    } catch (err) {
      setImportResult({
        success: false,
        created: 0,
        events: [],
        error: getErrorMessage(err),
      });
      setStep('results');
    } finally {
      setIsLoading(false);
    }
  }, [validationResults, onSuccess]);

  const validCount = validationResults.filter((r) => r.isValid).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white max-w-3xl w-full border border-hairline">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Import Events from CSV
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Step indicator */}
            <div className="flex items-center mb-6">
              {['upload', 'preview', 'results'].map((s, i) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`
                      flex items-center justify-center w-8 h-8 text-sm font-medium
                      ${
                        step === s
                          ? 'bg-primary-600 text-white'
                          : ['upload', 'preview', 'results'].indexOf(step) > i
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }
                    `}
                  >
                    {['upload', 'preview', 'results'].indexOf(step) > i ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span
                    className={`ml-2 text-sm ${
                      step === s ? 'font-medium text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {s === 'upload' && 'Upload'}
                    {s === 'preview' && 'Preview'}
                    {s === 'results' && 'Results'}
                  </span>
                  {i < 2 && (
                    <div className="w-12 h-px bg-gray-300 mx-4" />
                  )}
                </div>
              ))}
            </div>

            {/* Step: Upload */}
            {step === 'upload' && (
              <div>
                <CSVDropZone onFileSelect={handleFileSelect} disabled={isLoading} />

                {isLoading && (
                  <div className="mt-4 text-center text-gray-600">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-rack border-r-transparent mr-2" />
                    Parsing CSV...
                  </div>
                )}

                {parseResult && !parseResult.success && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Failed to parse CSV
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {parseResult.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={downloadTemplate}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Download CSV template
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                    Download a sample CSV with the correct format
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    Tip: when a row's <span className="font-medium">eventType</span>{' '}
                    matches one of your Event Types (Settings &rarr; Event Types,
                    capitalization doesn't matter), the imported event automatically
                    gets that type's crew roles.
                  </p>
                </div>
              </div>
            )}

            {/* Step: Preview */}
            {step === 'preview' && (
              <div>
                <CSVPreviewTable
                  validationResults={validationResults}
                  onRemoveRow={handleRemoveRow}
                />

                {validationResults.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    All rows have been removed.{' '}
                    <button
                      onClick={() => setStep('upload')}
                      className="text-primary-600 hover:underline"
                    >
                      Upload a new file
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step: Results */}
            {step === 'results' && importResult && (
              <div className="text-center py-6">
                {importResult.success ? (
                  <>
                    <div className="mx-auto w-16 h-16 bg-green-100 flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Import Successful
                    </h3>
                    <p className="text-gray-600">
                      Successfully created {importResult.created} event
                      {importResult.created !== 1 ? 's' : ''}.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto w-16 h-16 bg-red-100 flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Import Failed
                    </h3>
                    <p className="text-red-600">{importResult.error}</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div>
              {step === 'preview' && (
                <button
                  onClick={() => setStep('upload')}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Back to upload
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {step === 'results' ? 'Close' : 'Cancel'}
              </button>

              {step === 'preview' && validCount > 0 && (
                <button
                  onClick={handleImport}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                      Importing...
                    </>
                  ) : (
                    `Import ${validCount} Event${validCount !== 1 ? 's' : ''}`
                  )}
                </button>
              )}

              {step === 'results' && importResult?.success && (
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  Import More
                </button>
              )}

              {step === 'results' && !importResult?.success && (
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
